import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { HelpCircle } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { HANDBOOK_AREA, useHandbookAccess, useHandbookHelp } from "../hooks/use-handbook";

// The panel body (renderer, blocks) is its own chunk: every CRM page mounts the
// trigger, so only the button and one small cached request are eager.
const HelpPanel = lazy(() => import("./help-panel"));
const prefetchPanel = () => void import("./help-panel");

/**
 * "Help for this page" — mounted once, in AreaRoute, so every CRM page gets it
 * with no per-page edits. It appears only when published Handbook pages are
 * tagged with the page's area, and opens beside the page so a Care agent on a
 * call never loses their screen.
 */
export function PageHelp({ area }: { area: string }) {
  const { canRead, isLoaded } = useHandbookAccess();
  const enabled = isLoaded && canRead && area !== HANDBOOK_AREA;
  const { data: entries } = useHandbookHelp(area, enabled);
  const [open, setOpen] = useState(false);

  if (!enabled || !entries?.length) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        onMouseEnter={prefetchPanel}
        onFocus={prefetchPanel}
        className="fixed bottom-5 right-5 z-40 grid h-10 w-10 place-items-center rounded-full border bg-white text-slate-600 shadow-md transition-colors hover:border-orange-300 hover:text-orange-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        aria-label="Help for this page"
        title="Help for this page"
      >
        <HelpCircle className="h-5 w-5" aria-hidden />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="border-b px-5 py-4 text-left">
            <SheetTitle>Help for this page</SheetTitle>
            <SheetDescription>From the Handbook. Your page stays open underneath.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {open ? (
              <Suspense fallback={<p className="text-sm text-slate-500">Loading…</p>}>
                <HelpPanel entries={entries} onNavigate={() => setOpen(false)} />
              </Suspense>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
